/**
 * Activity — what this admin panel has done to this user, from our own audit
 * log. The legacy console keeps a log we cannot read, so before this tab
 * "did someone already handle this?" had no answer inside the tool.
 *
 * Failed attempts are shown too: an attempted deletion that errored is as
 * interesting to the next agent as one that worked.
 */
import { useQuery } from "@tanstack/react-query";
import { CircleAlert, Loader2 } from "lucide-react";
import {
  ACTIVITY_LABELS,
  knownEmails,
  type UserAccount,
  type UserActivityEntry,
} from "../../../../../shared/index";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, EmptyState } from "./parts";
import { EmailTag } from "./email-tag";
import { describeError, formatDate, relativeDate } from "./format";

export function ActivityTab({ account }: { account: UserAccount }) {
  // Actions taken before an email change were recorded against the old
  // address, so every address the account has used is queried.
  const emails = knownEmails(account);

  const activity = useQuery({
    queryKey: ["site-user-activity", emails.join(",")],
    queryFn: () => api.siteUsers.activity(emails),
    retry: false,
  });

  if (activity.isLoading) {
    return (
      <div className="flex items-center gap-2 p-8 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading activity…
      </div>
    );
  }

  if (activity.isError) {
    return (
      <div className="flex items-start gap-3 rounded-xl border border-destructive/40 bg-destructive/5 p-4 text-sm">
        <CircleAlert className="mt-0.5 h-4 w-4 text-destructive" />
        <p>{describeError(activity.error)}</p>
      </div>
    );
  }

  const entries = activity.data?.entries ?? [];

  return (
    <Card
      title="Admin activity"
      description="Actions taken on this user from this panel. Operations run in the legacy console are not included."
    >
      {entries.length === 0 ? (
        <EmptyState>
          Nothing has been done to this user from this panel yet.
        </EmptyState>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>When</TableHead>
              <TableHead>Who</TableHead>
              <TableHead>Action</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead>Result</TableHead>
              {emails.length > 1 && <TableHead>On</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {entries.map((e, i) => (
              <Row key={`${e.at}-${i}`} entry={e} emails={emails} />
            ))}
          </TableBody>
        </Table>
      )}
    </Card>
  );
}

function Row({
  entry,
  emails,
}: {
  entry: UserActivityEntry;
  emails: string[];
}) {
  return (
    <TableRow>
      <TableCell className="whitespace-nowrap">
        {formatDate(entry.at, true)}
        <span className="block text-xs text-muted-foreground">
          {relativeDate(entry.at)}
        </span>
      </TableCell>
      <TableCell className="font-medium">{entry.user}</TableCell>
      <TableCell>{ACTIVITY_LABELS[entry.action] ?? entry.action}</TableCell>
      <TableCell className="max-w-[18rem] truncate text-muted-foreground">
        {entry.reason ?? "—"}
      </TableCell>
      <TableCell>
        {entry.result === "ok" ? (
          <Badge variant="success">ok</Badge>
        ) : (
          <Badge variant="destructive" title={entry.detail ?? undefined}>
            failed
          </Badge>
        )}
      </TableCell>
      {emails.length > 1 && (
        <TableCell>
          <EmailTag
            email={entry.target}
            all={emails}
            current={entry.target.toLowerCase() === emails[0]?.toLowerCase()}
          />
        </TableCell>
      )}
    </TableRow>
  );
}
